import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import { cn } from '@/utils/cn';
import { FloatingListbox } from '@/components/ui/FloatingListbox';
import { getPhoneCountry, filterPhoneCountries } from '@/utils/phoneCountries';
import {
  buildE164,
  parsePhoneValue,
  sanitizeNationalInput,
  validateNationalNumber,
} from '@/utils/phoneNumber';

export interface PhoneInputProps {
  label?: string;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  /** Override org/browser default country (ISO code, e.g. IN). */
  defaultCountryCode?: string;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    label,
    value = '',
    onChange,
    onBlur,
    name,
    id,
    error,
    hint,
    disabled,
    required,
    className,
    autoFocus,
    placeholder,
    defaultCountryCode,
    onKeyDown,
  },
  ref,
) {
  const { t } = useTranslation('common');
  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { required: isRequired } = parseFieldLabel(label, required);
  const resolvedDefaultCountry = useDefaultPhoneCountry(defaultCountryCode);

  const parsed = useMemo(
    () => parsePhoneValue(value, resolvedDefaultCountry),
    [value, resolvedDefaultCountry],
  );

  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [national, setNational] = useState(parsed.national);
  const [localError, setLocalError] = useState<string | undefined>();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const next = parsePhoneValue(value, resolvedDefaultCountry);
    setCountryCode(next.countryCode);
    setNational(next.national);
    lastEmitted.current = value;
  }, [value, resolvedDefaultCountry]);

  useEffect(() => {
    if (value.trim()) return;
    const next = parsePhoneValue('', resolvedDefaultCountry);
    setCountryCode(next.countryCode);
    setNational('');
    lastEmitted.current = '';
  }, [value, resolvedDefaultCountry]);

  const country = getPhoneCountry(countryCode);
  const displayError = error ?? localError;

  const filteredCountries = useMemo(() => filterPhoneCountries(search), [search]);
  const listboxId = `${inputId}-country-listbox`;
  const optionId = (code: string) => `${listboxId}-option-${code}`;

  useEffect(() => {
    if (!pickerOpen) {
      setSearch('');
      setActiveIndex(-1);
      return;
    }
    const selectedIndex = filteredCountries.findIndex((item) => item.code === countryCode);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : filteredCountries.length > 0 ? 0 : -1);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [countryCode, filteredCountries, pickerOpen]);

  useEffect(() => {
    if (!pickerOpen || activeIndex < 0) return;
    const activeCode = filteredCountries[activeIndex]?.code ?? '';
    document
      .getElementById(`${listboxId}-option-${activeCode}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, filteredCountries, listboxId, pickerOpen]);

  const emitChange = useCallback(
    (nextCountry: string, nextNational: string) => {
      const e164 = buildE164(nextCountry, nextNational);
      lastEmitted.current = e164;
      onChange?.({
        target: { value: e164, name: name ?? '' },
      } as ChangeEvent<HTMLInputElement>);
    },
    [name, onChange],
  );

  const handleCountryChange = (nextCountry: string) => {
    setCountryCode(nextCountry);
    const trimmedNational = sanitizeNationalInput(nextCountry, national);
    setNational(trimmedNational);
    setLocalError(undefined);
    emitChange(nextCountry, trimmedNational);
    setPickerOpen(false);
    setSearch('');
  };

  const handleNationalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextNational = sanitizeNationalInput(countryCode, event.target.value);
    setNational(nextNational);
    setLocalError(undefined);
    emitChange(countryCode, nextNational);
  };

  const handlePickerKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape' && pickerOpen) {
      event.preventDefault();
      event.stopPropagation();
      setPickerOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === 'Tab') {
      setPickerOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!pickerOpen) {
        setPickerOpen(true);
        return;
      }
      if (filteredCountries.length === 0) return;
      setActiveIndex((current) => {
        const start = current < 0 ? (event.key === 'ArrowDown' ? -1 : 0) : current;
        return event.key === 'ArrowDown'
          ? (start + 1) % filteredCountries.length
          : (start - 1 + filteredCountries.length) % filteredCountries.length;
      });
      return;
    }
    if (event.key === 'Home' && pickerOpen) {
      event.preventDefault();
      setActiveIndex(filteredCountries.length > 0 ? 0 : -1);
      return;
    }
    if (event.key === 'End' && pickerOpen) {
      event.preventDefault();
      setActiveIndex(filteredCountries.length - 1);
      return;
    }
    if (event.key === 'Enter' && pickerOpen && activeIndex >= 0) {
      event.preventDefault();
      handleCountryChange(filteredCountries[activeIndex].code);
      triggerRef.current?.focus();
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (national) {
      setLocalError(validateNationalNumber(countryCode, national));
    } else {
      setLocalError(undefined);
    }
    onBlur?.(event);
  };

  return (
    <div className={cn('w-full', className)}>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <div ref={anchorRef}>
        <div
          className={cn(
            'input flex items-stretch gap-0 p-0',
            displayError && 'input-error',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          role="combobox"
          aria-label={`${country.name}, +${country.dialCode || ''}. Change country`}
          aria-haspopup="listbox"
          aria-expanded={pickerOpen}
          aria-controls={pickerOpen ? listboxId : undefined}
          aria-activedescendant={
            pickerOpen && activeIndex >= 0
              ? optionId(filteredCountries[activeIndex].code)
              : undefined
          }
          onKeyDown={handlePickerKeyDown}
          onClick={() => {
            if (!disabled) setPickerOpen((open) => !open);
          }}
          className={cn(
            'flex shrink-0 items-center gap-1 rounded-l-lg py-2 pl-2.5 pr-2',
            'text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800/60',
            disabled && 'pointer-events-none',
          )}
        >
          <CountryFlag code={country.code} variant="field" />
          <KeyboardArrowDownIcon
            sx={{ fontSize: 18 }}
            className={cn('text-slate-500 transition-transform', pickerOpen && 'rotate-180')}
          />
          <span className="text-sm font-medium tabular-nums">
            {country.dialCode ? `+${country.dialCode}` : '+'}
          </span>
        </button>

        <div className="my-2 w-px shrink-0 self-stretch bg-slate-200 dark:bg-slate-600" aria-hidden />

        <input
          ref={ref}
          id={inputId}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          disabled={disabled}
          required={isRequired}
          value={national}
          maxLength={country.nationalLength}
          placeholder={placeholder}
          onChange={handleNationalChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          aria-invalid={displayError ? true : undefined}
          aria-describedby={displayError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-slate-400"
        />
        </div>

        <FloatingListbox
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            anchorRef={anchorRef}
            id={`${listboxId}-popover`}
          >
            <div className="border-b border-slate-200 p-2 dark:border-slate-700">
              <div className="relative">
                <SearchIcon
                  sx={{ fontSize: 16 }}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    handlePickerKeyDown(e);
                  }}
                  placeholder={t('labels.searchCountryOrCode')}
                  autoComplete="off"
                  className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/30 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
            <ul
              id={listboxId}
              role="listbox"
              aria-label={t('labels.selectCountry')}
              className="min-h-0 flex-1 overflow-y-auto py-1"
            >
              {filteredCountries.length === 0 ? (
                <li className="px-3 py-2 text-sm text-slate-500">No matches</li>
              ) : (
                filteredCountries.map((c) => (
                  <li key={c.code} id={optionId(c.code)} role="option" aria-selected={c.code === countryCode}>
                    <button
                      type="button"
                      onClick={() => handleCountryChange(c.code)}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                        c.code === countryCode && 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200',
                        activeIndex >= 0 &&
                          filteredCountries[activeIndex]?.code === c.code &&
                          'bg-slate-100 dark:bg-slate-800',
                      )}
                    >
                      <CountryFlag code={c.code} variant="list" />
                      <span className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-slate-100">
                        {c.name}
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                        +{c.dialCode}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
        </FloatingListbox>
      </div>
      {hint && !displayError ? (
        <p id={`${inputId}-hint`} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
      {displayError ? (
        <p id={`${inputId}-error`} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {displayError}
        </p>
      ) : null}
    </div>
  );
});

PhoneInput.displayName = 'PhoneInput';
